import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { CompleteProfileDialog } from '../features/auth/CompleteProfileDialog';
import { NoAccessScreen, type NoAccessReason } from '../features/auth/NoAccessScreen';
import { ensureDefaultEnrollment } from '../shared/auth/account-api';
import { STUDENT_EMAIL_DOMAIN } from '../shared/auth/student-login';
import { ACCOUNT_STATUS_QUERY_KEY, useAccountStatus } from '../shared/hooks/useAccountStatus';
import { PageLoader } from '../shared/ui/PageLoader';
import { StatusBanner } from '../shared/ui/StatusBanner';

import s from './AccountGate.module.css';
import { useAuth } from './AuthProvider';
import { SessionRecovering } from './SessionRecovering';

interface AccountGateProps {
  children: ReactNode;
}

/**
 * What to call this account in the profile dialog.
 *
 * Credential students have no real email — Phase 90 signs them in with a
 * synthetic `<handle>@<slug>.students.internal` address. Showing that back to
 * them ("Finish setting up ram@acme.students.internal") exposes plumbing they
 * never chose, and they are exactly the population this dialog newly blocks
 * (their profiles were stored with phone = '').
 */
function accountLabelFor(displayName: string, email: string | undefined): string {
  if (displayName !== '') return displayName;
  if (email === undefined || email.endsWith(STUDENT_EMAIL_DOMAIN)) return 'your account';
  return email;
}

/**
 * The name the identity provider already told us, straight from the JWT.
 *
 * profiles.display_name is populated by handle_new_user from
 * raw_user_meta_data->>'name' at INSERT time, which is not a reliable moment for
 * OAuth — so a Google user can easily reach the dialog with an empty stored
 * name and be asked to type one Google already handed over. The session's
 * user_metadata is the authoritative client-side copy; prefer the stored name
 * (the user may have corrected it) and fall back to this.
 */
function providerName(metadata: Record<string, unknown> | undefined): string {
  const candidate = metadata?.['name'] ?? metadata?.['full_name'];
  return typeof candidate === 'string' ? candidate.trim() : '';
}

/**
 * The single gate every authenticated route passes through.
 *
 * It replaces the RequireAuth -> RequirePhoneVerified stack, which rendered
 * three different full-screen states in sequence on a cold refresh (PageLoader,
 * then "Checking phone verification…", then RequireRole's "Loading…"). Every
 * unresolved branch here renders the SAME <PageLoader />, so nothing swaps
 * mid-load.
 *
 * It also fixes the redirect itself: the old guard sent the user to
 * /verify-phone whenever `verified !== true`, which included `null` — an
 * UNKNOWN answer treated as a confirmed negative, producing the "flashes the
 * phone screen then comes back" bug. Here every redirect requires an
 * affirmative answer from the server.
 */
export function AccountGate({ children }: AccountGateProps): JSX.Element {
  const { session, user, loading: authLoading, unreachable } = useAuth();
  const { status, loading: statusLoading, error } = useAccountStatus(user?.id ?? null);
  const location = useLocation();
  const queryClient = useQueryClient();

  // One attempt per mounted session. Without the ref, a re-render between the
  // RPC resolving and the refetched status arriving would fire it again.
  const enrollAttempted = useRef(false);
  // 'settled' covers BOTH outcomes that leave the user roleless: the RPC threw,
  // and the RPC succeeded with 'not_configured'. Tracking only failure would
  // spin on the loader forever in the second case, which is the exact state a
  // platform with no default institute is in.
  const [enrollSettled, setEnrollSettled] = useState(false);

  const needsEnrollment =
    status !== null && status.role === null && !status.membershipPending && status.profileComplete;

  useEffect(() => {
    if (!needsEnrollment || enrollAttempted.current) return;
    enrollAttempted.current = true;
    void (async () => {
      try {
        const result = await ensureDefaultEnrollment();
        if (result === 'enrolled' || result === 'already_assigned') {
          await queryClient.invalidateQueries({ queryKey: ACCOUNT_STATUS_QUERY_KEY });
        }
      } catch {
        /* fall through to NoAccessScreen, which explains the state and offers sign-out */
      } finally {
        setEnrollSettled(true);
      }
    })();
  }, [needsEnrollment, queryClient]);

  if (authLoading || (session !== null && statusLoading)) {
    return <PageLoader />;
  }

  // See RequireAuth: an unverifiable session is not an absent one.
  if (session === null && unreachable) {
    return <SessionRecovering />;
  }

  if (session === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (status === null) {
    return (
      <div className={s.shell}>
        <div className={s.card}>
          <StatusBanner tone="error">
            {error?.message ?? 'Could not load your account. Try signing in again.'}
          </StatusBanner>
        </div>
      </div>
    );
  }

  // Name + phone first: enrolment and the OTP gate both depend on having them,
  // and an institute that cannot phone its students has a half-useful roster.
  if (!status.profileComplete) {
    const name =
      status.displayName !== '' ? status.displayName : providerName(session.user.user_metadata);
    return (
      <CompleteProfileDialog
        userId={session.user.id}
        initialName={name}
        initialPhone={status.phone}
        // Deliberately the STORED name, not `name`: when the provider fallback
        // supplies one, the dialog already shows it on its own row, and a
        // subtitle repeating it would cost us the email that says which account
        // this actually is.
        accountLabel={accountLabelFor(status.displayName, session.user.email)}
      />
    );
  }

  // Phase 184 removed the phone-OTP gate that used to sit here. It was never a
  // security control — the code was always '1234' and otp-send returned it in
  // the response body (docs/KNOWN_ISSUES.md §11) — and Supabase's own "Confirm
  // email" now stands between signup and a session, which is a stronger claim
  // about a real person than a code the API just handed you.
  //
  // `status.needsOtp` and `status.phoneVerified` still exist and are still
  // populated: the phone_verified table holds real history, and a future SMS
  // feature will want the same shape. Only the gate moved.

  if (status.role === null) {
    // While the enrolment RPC is in flight the status still says role: null —
    // keep showing the loader rather than flashing the dead-end screen.
    if (needsEnrollment && !enrollSettled) {
      return <PageLoader label="Setting up your account…" />;
    }
    const reason: NoAccessReason = status.membershipPending
      ? 'pending_approval'
      : status.defaultInstituteReady
        ? 'unknown'
        : 'not_configured';
    return <NoAccessScreen reason={reason} />;
  }

  return <>{children}</>;
}
